import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  Select,
  MenuItem,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import dayjs from "dayjs";
import { EmployeeDataContext } from "../../customHooks/dataProviders/EmployeeDataProvider";
import { GetManagerName } from "../../service/api/emsService/EmployeeLeaveService";
import {
  searchGoals,
  setUpGoals,
} from "../../service/api/emsService/GoalService";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";

const EmployeeGoals = () => {
  const { empBasicDetailId } = useParams();
  const { employeeData } = useContext(EmployeeDataContext);
  const navigate = useNavigate();

  const effectiveEmpId =
    empBasicDetailId || employeeData?.employeeBasicDetailId;

  const [managerName, setManagerName] = useState("");
  const [goals, setGoals] = useState([]);
  const [addedGoals, setAddedGoals] = useState([]);
  const [goalStatus, setGoalStatus] = useState("");
  const [goalsFetchFailed, setGoalsFetchFailed] = useState(false);

  const [employeeDetailsFromGoals, setEmployeeDetailsFromGoals] = useState({
    employeeCode: "",
    fullName: "",
    emailId: "",
  });

  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [openDialog, setOpenDialog] = useState(false);

  const fetchManagerName = useCallback(async () => {
    try {
      if (!effectiveEmpId) return;
      const response = await GetManagerName(effectiveEmpId, "leave", {
        additionalInfo: "fetching for goals screen",
      });
      setManagerName(response?.approverName || "N/A");
    } catch (error) {
      console.error("Error fetching manager name:", error);
      setManagerName("N/A");
    }
  }, [effectiveEmpId]);

  const fetchGoalsFromAPI = useCallback(async () => {
    try {
      const payload = {
        empBasicDetailId: effectiveEmpId,
        page: 0,
        size: 10,
      };
      const response = await searchGoals(payload);
      if (!response?.content?.length) {
        setGoalsFetchFailed(true);
        return;
      }

      const setup = response.content[0];
      setEmployeeDetailsFromGoals({
        employeeCode: setup.employeeCode || "",
        fullName: setup.fullName || "",
        emailId: setup.emailId || "",
      });

      const formattedGoals = [];
      response.content.forEach((setup) => {
        setGoalStatus(setup.status);
        setup.empGoalDetailsEntity?.forEach((detail) => {
          formattedGoals.push({
            goal: detail.goal,
            empGoalSetupId: detail.empGoalSetupId,
            empGoalDetailsId: detail.empGoalDetailsId,
            fromDate: dayjs(detail.fromDate),
            toDate: dayjs(detail.toDate),
            percentage: detail.percentage || 0,
            goalDescription: detail.goalDescription || "",
            managerConsent: detail.managerComments,
            managerComments: detail.managerComments || "",
            editable: false,
          });
        });
      });
      setGoals(formattedGoals);
      setGoalsFetchFailed(false);
    } catch (error) {
      console.error("Failed to fetch goals:", error);
      setGoalsFetchFailed(true);
    }
  }, [effectiveEmpId]);

  useEffect(() => {
    fetchManagerName();
    fetchGoalsFromAPI();
  }, [fetchManagerName, fetchGoalsFromAPI]);

  const handleAddGoalRow = useCallback(() => {
    setTimeout(() => {
      setAddedGoals((prev) => [
        ...prev,
        {
          goal: "",
          fromDate: dayjs(),
          toDate: dayjs().add(30, "day"),
          percentage: 0,
          goalDescription: "",
          managerConsent: null,
          managerComments: "",
          editable: true,
        },
      ]);
    }, 0);
  }, []);

  const updateGoalField = (index, field, value) => {
    const all = [...goals, ...addedGoals];
    const isEditable = all[index]?.editable;
    if (isEditable) {
      const updated = [...addedGoals];
      updated[index - goals.length][field] = value;
      setAddedGoals(updated);
    } else {
      const updated = [...goals];
      updated[index][field] = value;
      setGoals(updated);
    }
  };

  const handleDeleteRow = (index) => {
    const editableStartIndex = goals.length;
    const addedGoalIndex = index - editableStartIndex;
    if (addedGoalIndex >= 0) {
      const updated = [...addedGoals];
      updated.splice(addedGoalIndex, 1);
      setAddedGoals(updated);
    }
  };

  const handleOpenDialog = (index, _, action) => {
    setSelectedRowIndex(index);
    setSelectedAction(action);
    const all = [...goals, ...addedGoals];
    const target = all[index];
    setRemarks(target.managerComments || "");
    setOpenDialog(true);
  };

  const handleConfirmAction = () => {
    const all = [...goals, ...addedGoals];
    const target = all[selectedRowIndex];
    target.managerComments = remarks;
    target.managerConsent = selectedAction;
    setOpenDialog(false);
  };

  const isGoalDisabled = (field) => {
    if (["128", "130"].includes(goalStatus)) {
      return [
        "goal",
        "fromDate",
        "toDate",
        "percentage",
        "goalDescription",
      ].includes(field);
    } else if (goalStatus === "127") {
      return ["managerConsent", "managerComments"].includes(field);
    } else if (goalStatus === "129") {
      return [
        "goal",
        "fromDate",
        "toDate",
        "percentage",
        "managerConsent",
        "managerComments",
        "goalDescription",
      ].includes(field);
    } else if (goalStatus === "131") {
      return true;
    }
    return false;
  };

  const getPayloadStatus = () => {
    if (goalStatus === "127") return "128";
    if (goalStatus === "128") return "129";
    if (goalStatus === "129") return "130";
    return goalStatus;
  };

  const getTotalExistingPercentage = () =>
    goals.reduce((acc, g) => acc + Number(g.percentage || 0), 0);

  const handleSaveGoals = async () => {
    const allGoals = [...goals, ...addedGoals];
    const goalSetUpDetails = allGoals.map((goal, idx) => {
      const goalFromAPI = goals[idx];
      return {
        empGoalDetailId: goalFromAPI ? goalFromAPI.empGoalDetailsId : 0,
        goalSetUpId: goalFromAPI ? goalFromAPI.empGoalSetupId : 0,
        goal: goal.goal,
        goalType: "Basic",
        goalDescription: goal.goalDescription || "",
        percentage: goal.percentage,
        comments: goal.managerComments || "",
        status: getPayloadStatus(),
        managerComments: goal.managerComments || "",
        managerConsent: goal.managerConsent,
        createdBy: "admin",
        fromDate: goal.fromDate.format("YYYY-MM-DD"),
        toDate: goal.toDate.format("YYYY-MM-DD"),
      };
    });

    if (getTotalExistingPercentage() > 100) {
      toast.error("Total existing goal percentage exceeds 100%");
      return;
    }

    const payload = {
      empGoalSetupId: goals[0]?.empGoalSetupId || 0,
      approvedBy: 0,
      approvedDate: dayjs().format("YYYY-MM-DD"),
      approverComments: "",
      comments: "",
      emplBasicId: effectiveEmpId,
      status: getPayloadStatus(),
      submittedDate: dayjs().format("YYYY-MM-DD"),
      createdBy: employeeData?.emailId,
      fromDate: goals[0]?.fromDate.format("YYYY-MM-DD"),
      toDate: goals[0]?.toDate.format("YYYY-MM-DD"),
      goalSetUpDetails,
    };

    try {
      await setUpGoals(payload);
      toast.success("Goals saved successfully!");
      navigate("/home");
    } catch (error) {
      toast.error("Error saving goals. Please try again.");
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box p={3}>
        <Typography variant="h5" gutterBottom>
          Employee Goals Overview
        </Typography>

        <Paper variant="outlined" sx={{ mb: 3, p: 2 }}>
          <Grid container spacing={2}>
            {["Employee Code", "Employee Name", "Email", "Manager Name"].map(
              (label, idx) => (
                <Grid item xs={12} sm={6} md={3} key={label}>
                  <TextField
                    label={label}
                    fullWidth
                    disabled
                    value={
                      [
                        employeeDetailsFromGoals.employeeCode,
                        employeeDetailsFromGoals.fullName,
                        employeeDetailsFromGoals.emailId,
                        managerName,
                      ][idx]
                    }
                  />
                </Grid>
              )
            )}
          </Grid>
        </Paper>

        <Box sx={{ maxHeight: 400, overflow: "auto" }}>
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table>
              <TableHead>
                <TableRow>
                  {[
                    "Goal",
                    "Goal Comments",
                    "From",
                    "To",
                    "Percentage",
                    "Manager Consent",
                    "Manager Comments",
                    "Actions",
                  ].map((header) => (
                    <TableCell
                      key={header}
                      align="center"
                      sx={{ fontWeight: "bold" }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[...goals, ...addedGoals].map((goal, idx) => (
                  <TableRow
                    key={idx}
                    sx={{
                      backgroundColor: goal.editable ? "#e3f2fd" : "#f3e5f5",
                    }}
                  >
                    <TableCell align="center">
                      <TextField
                        value={goal.goal}
                        size="small"
                        disabled={!goal.editable || isGoalDisabled("goal")}
                        onChange={(e) =>
                          updateGoalField(idx, "goal", e.target.value)
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={goal.goalDescription || ""} arrow>
                        <TextField
                          value={goal.goalDescription || ""}
                          size="small"
                          multiline
                          maxRows={3}
                          onChange={(e) =>
                            updateGoalField(
                              idx,
                              "goalDescription",
                              e.target.value
                            )
                          }
                          disabled={!goal.editable}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <DatePicker
                        value={goal.fromDate}
                        onChange={(val) =>
                          updateGoalField(idx, "fromDate", val)
                        }
                        format="DD/MM/YYYY"
                        slotProps={{
                          textField: { size: "small", sx: { maxWidth: 150 } },
                        }}
                        disabled={isGoalDisabled("fromDate")}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <DatePicker
                        value={goal.toDate}
                        onChange={(val) => updateGoalField(idx, "toDate", val)}
                        format="DD/MM/YYYY"
                        slotProps={{
                          textField: { size: "small", sx: { maxWidth: 150 } },
                        }}
                        disabled={isGoalDisabled("toDate")}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        type="number"
                        value={
                          goal.percentage === 0 ? "0" : goal.percentage || ""
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "") {
                            updateGoalField(idx, "percentage", "");
                          } else {
                            const parsed = Number(value);
                            if (!isNaN(parsed)) {
                              updateGoalField(idx, "percentage", parsed);
                            }
                          }
                        }}
                        size="small"
                        inputProps={{
                          min: 0,
                          max: 100,
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                          style: {
                            MozAppearance: "textfield",
                          },
                        }}
                        sx={{
                          "& input[type=number]": {
                            MozAppearance: "textfield",
                          },
                          "& input[type=number]::-webkit-outer-spin-button": {
                            WebkitAppearance: "none",
                            margin: 0,
                          },
                          "& input[type=number]::-webkit-inner-spin-button": {
                            WebkitAppearance: "none",
                            margin: 0,
                          },
                        }}
                        disabled={isGoalDisabled("percentage")}
                        error={
                          !goal.editable && getTotalExistingPercentage() > 100
                        }
                        helperText={
                          !goal.editable && getTotalExistingPercentage() > 100
                            ? "Total % exceeds 100"
                            : ""
                        }
                      />
                    </TableCell>

                    <TableCell align="center">
                      {goal.managerConsent || (
                        <>
                          <Tooltip title="Approve">
                            <IconButton
                              onClick={() =>
                                handleOpenDialog(idx, "manager", "Approved")
                              }
                              sx={{ color: "#4caf50" }}
                              disabled={isGoalDisabled("managerConsent")}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              onClick={() =>
                                handleOpenDialog(idx, "manager", "Rejected")
                              }
                              sx={{ color: "#f44336" }}
                              disabled={isGoalDisabled("managerConsent")}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        value={goal.managerComments || ""}
                        size="small"
                        multiline
                        maxRows={3}
                        onChange={(e) =>
                          updateGoalField(
                            idx,
                            "managerComments",
                            e.target.value
                          )
                        }
                        disabled={isGoalDisabled("managerComments")}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {goal.editable && (
                        <Tooltip title="Delete">
                          <span>
                            <IconButton
                              onClick={() => handleDeleteRow(idx)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Box mt={2} textAlign="right">
          <Typography
            variant="subtitle2"
            color={getTotalExistingPercentage() > 100 ? "error" : "textPrimary"}
          >
            Existing Goals Total: {getTotalExistingPercentage()}%
          </Typography>
        </Box>
        {!goalsFetchFailed && (goals.length > 0 || addedGoals.length > 0) && (
          <Box display="flex" justifyContent="space-between" mt={2}>
            <Button
              variant="contained"
              onClick={handleAddGoalRow}
              disabled={goalStatus === "128"}
            >
              + Add Goal
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveGoals}
            >
              {goalStatus === "127"
                ? "Send to Manager"
                : goalStatus === "128"
                ? "Approve"
                : goalStatus === "129"
                ? "Approve"
                : "Submit"}
            </Button>
          </Box>
        )}

        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>Confirm {selectedAction} by Manager</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button
              onClick={handleConfirmAction}
              variant="contained"
              color={selectedAction === "Approved" ? "success" : "error"}
              disabled={!remarks.trim()}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default EmployeeGoals;
